/**
 * Application Constants
 * Centralized location for all constant values used across the application
 */

// Website Information
export const WEBSITE_NAME = 'Vardhman';
export const SITE_DESCRIPTION = 'All-in-one e-commerce platform for Amazon, Walmart, and TikTok Shop sellers';
export const CURRENT_YEAR = new Date().getFullYear();

// API Configuration
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.vardhman.com/v1';
export const API_TIMEOUT = 30000; // 30 seconds

// Navigation Constants
export const NAV_ITEMS = [
  { title: 'Get Started', path: '/get-started', hasDropdown: true },
  { title: 'Solutions', path: '/solutions', hasDropdown: true },
  { title: 'Pricing', path: '/#pricing', hasDropdown: false },
  { title: 'Resources', path: '/resources', hasDropdown: true },
  { title: 'Blog', path: '/blog', hasDropdown: false },
  { title: 'About', path: '/about', hasDropdown: true }
];

// Pricing Plans Data
export const PRICING_PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'Perfect for new sellers',
    monthlyPrice: '29',
    yearlyPrice: '23',
    features: [
      'Basic product research',
      'Keyword tracking for up to 10 products',
      'Daily sales estimates',
      'Basic analytics dashboard',
      'Email support',
      'Community access'
    ],
    buttonText: 'Start Free Trial',
    popular: false
  },
  {
    id: 'professional',
    name: 'Professional',
    tagline: 'For growing businesses',
    monthlyPrice: '79',
    yearlyPrice: '59',
    features: [
      'Advanced product research',
      'Keyword tracking for up to 50 products',
      'Real-time sales estimates',
      'Advanced analytics dashboard',
      'Priority email support',
      'Inventory management',
      'PPC campaign tracking',
      'Competitor analysis'
    ],
    buttonText: 'Get Started',
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'For large scale operations',
    monthlyPrice: '199',
    yearlyPrice: '149',
    features: [
      'Enterprise product research',
      'Unlimited keyword tracking',
      'Real-time sales analytics',
      'Custom analytics dashboard',
      '24/7 phone & email support',
      'Advanced inventory management',
      'AI-powered insights',
      'API access',
      'Custom integrations',
      'Dedicated account manager'
    ],
    buttonText: 'Contact Sales',
    popular: false
  }
];

// FAQ Data
export const FAQ_DATA = [
  {
    question: 'What marketplaces does Vardhman support?',
    answer: 'Vardhman supports Amazon (all marketplaces), Walmart.com, and TikTok Shop. We\'re continuously adding support for new e-commerce platforms to help you expand your business.'
  },
  {
    question: 'How accurate are your sales estimates?',
    answer: 'Our sales estimates are powered by advanced machine learning algorithms with an average accuracy of 85-95%. Accuracy may vary based on marketplace data availability and product category.'
  },
  {
    question: 'Can I try Vardhman for free?',
    answer: 'Yes! We offer a 14-day free trial for all our plans. No credit card required to start. You\'ll get full access to all features during your trial period.'
  },
  {
    question: 'How does the Chrome Extension work?',
    answer: 'Our free Chrome Extension allows you to analyze products directly on Amazon, Walmart, and TikTok Shop. It provides instant access to sales estimates, competition analysis, and profitability calculations without leaving the product page.'
  },
  {
    question: 'Do you offer training and support?',
    answer: 'Yes! We provide comprehensive onboarding, video tutorials, webinars, and 24/7 email support. Professional and Enterprise plans include priority support and dedicated account management.'
  },
  {
    question: 'Can I cancel my subscription anytime?',
    answer: 'Absolutely. You can cancel your subscription at any time from your account settings. There are no cancellation fees, and you\'ll continue to have access until the end of your billing period.'
  }
];

// Social Media Links
export const SOCIAL_LINKS = {
  twitter: 'https://twitter.com/vardhman',
  facebook: 'https://facebook.com/vardhman',
  youtube: 'https://youtube.com/vardhman',
  instagram: 'https://instagram.com/vardhman',
  linkedin: 'https://linkedin.com/company/vardhman'
};

// Color Constants
export const COLORS = {
  primary: '#3B82F6',
  primaryDark: '#2563EB',
  secondary: '#8B5CF6',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  dark: '#111827',
  light: '#F9FAFB'
};

// Breakpoints for Responsive Design
export const BREAKPOINTS = {
  xs: '320px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  xxl: '1536px'
};

// Feature Constants
export const FEATURES = [
  {
    id: 1,
    title: 'Customer Growth',
    description: 'Turn social views into e-commerce revenue with our advanced conversion tracking.',
    icon: '👥',
    color: '#10B981'
  },
  {
    id: 2,
    title: 'Product Discovery',
    description: 'Find trending products ahead of competitors with real-time market insights.',
    icon: '🔍',
    color: '#8B5CF6'
  },
  {
    id: 3,
    title: 'Inventory Management',
    description: 'AI predicts inventory needs and prevents stockouts with smart forecasting.',
    icon: '📦',
    color: '#F59E0B'
  },
  {
    id: 4,
    title: 'PPC Optimization',
    description: 'Maximize ad spend with AI-powered PPC optimization and A/B testing.',
    icon: '📈',
    color: '#3B82F6'
  }
];

// Marketplace Constants
export const MARKETPLACES = [
  {
    name: 'Amazon',
    icon: '🛍️',
    supported: true,
    countries: ['US', 'UK', 'CA', 'DE', 'FR', 'IT', 'ES', 'JP', 'AU', 'MX', 'BR']
  },
  {
    name: 'Walmart',
    icon: '🏪',
    supported: true,
    countries: ['US']
  },
  {
    name: 'TikTok Shop',
    icon: '🎵',
    supported: true,
    countries: ['US', 'UK']
  }
];

// Performance Metrics
export const PERFORMANCE_METRICS = {
  totalUsers: '4M+',
  averageRating: '4.5',
  countriesSupported: 12,
  integrationPartners: 50
};

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'vardhman_auth_token',
  USER_DATA: 'vardhman_user_data',
  CART_ITEMS: 'vardhman_cart_items',
  RECENT_SEARCHES: 'vardhman_recent_searches'
};

// Validation Constants
export const VALIDATION = {
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PASSWORD_MIN_LENGTH: 8,
  PHONE_REGEX: /^\+?[\d\s\-\(\)]{10,}$/
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  UNAUTHORIZED: 'Your session has expired. Please log in again.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'Server error. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.'
};

// Success Messages
export const SUCCESS_MESSAGES = {
  SIGNUP_SUCCESS: 'Account created successfully! Please check your email to verify your account.',
  LOGIN_SUCCESS: 'Login successful! Redirecting to dashboard...',
  PASSWORD_RESET_SENT: 'Password reset instructions have been sent to your email.',
  PROFILE_UPDATED: 'Profile updated successfully!',
  SUBSCRIPTION_UPDATED: 'Subscription updated successfully!'
};